import { useFormikContext, useField } from 'formik';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { ChangeEvent, memo, useEffect, useRef } from 'react';
import { useLocale } from '../../../../context/Localization/localization';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { WebFormPasswordFieldProps } from '../FormPasswordField.types';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const WebFormPasswordField = memo(
    ({
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled,
        required,
        autoFocus,
        capsLock,
        fullWidth,
        seventyFivePercentWidth,
        onChangeCallback,
        tabFocusRefContext,
    }: WebFormPasswordFieldProps) => {
        /**
         * Not all formik validation accounts for TextFields that are null.
         * So even though we pass an undefined validation type to the function,
         * it is still required in order to have null required fields validate.
         */
        const [field, meta, helper] = useField<string>({
            name,
            validate: TextFieldValidation({
                required: required ?? false,
                label,
                name,
                validationType: undefined,
            }),
        });
        const { submitCount } = useFormikContext();

        /**
         * A ref to the HTMLInputElement in the MUI TextField.
         */
        const inputRef = useRef<HTMLInputElement>(null);
        /**
         * The input value ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal value state.
         *
         * May not be needed.
         */
        const valueRef = useRef<string>(field.value ?? '');
        /**
         * The caret position ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal caret position state.
         *
         * * May not be needed.
         */
        const caretPositionRef = useRef<number | null>(null);
        useScrollToOnError({
            scrollable: inputRef,
            name: name,
        });

        const locale = useLocale();
        const {
            dealer: { primaryLanguage },
        } = useKioskState();

        useEffect(() => {
            if (tabFocusRefContext) {
                tabFocusRefContext.current = inputRef.current;
            }
        }, [tabFocusRefContext]);

        useEffect(() => {
            /**
             * Updates the internal caret position when the internal MUI value finishes changing from the
             * pattern mask.
             *
             * May not be needed.
             */
            if (inputRef.current) {
                inputRef.current.setSelectionRange(caretPositionRef.current, caretPositionRef.current);
            }
        }, [inputRef.current?.value]);

        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onChangeCallback && onChangeCallback();
            caretPositionRef.current = event.target.selectionEnd;
            valueRef.current = event.target.value;
            helper.setValue(event.target.value);
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'text'}
                type={'password'}
                inputRef={inputRef}
                value={valueRef.current}
                error={meta.error}
                touched={meta.touched || submitCount > 0}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onChange={handleOnChange}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
            />
        );
    },
);
